import { useMemo } from 'react'

import type { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { useApolloClient } from '@apollo/client'
import { useMatomo } from '@jonkoops/matomo-tracker-react'
import { Box, useTheme } from '@mui/material'
import { FormattedMessage } from 'react-intl'
import { useLocation } from 'react-router-dom'

import {
  AcreSquareLogo,
  ExternalLink,
  FullPageLoadingSpinner,
  Nav,
  OptionsNavLink,
  ScoreWordMarkHover,
  trackEventFiltered,
} from '@acre/design-system'
import { useGetCurrentUserQuery, useGetNotificationsQuery } from '@acre/graphql'
import { Flag, getUserOrganisationIds, useFlag, useKeyboardShortcut, useOpen, useUserContext } from '@acre/utils'

import GlobalSearchFieldAnchor from '@broker-crm-common/GlobalSearch/GlobalSearchFieldAnchor/GlobalSearchFieldAnchor'
import type { PermissionInput } from '@broker-crm-common/Permission/permission.types'
import usePermissionResolution from '@broker-crm-common/Permission/usePermissionResolution'
import { useNotifications, userType } from '@broker-crm-utils'

import useSourcingUrl from '../../utils/hooks/useSourcingUrl'
import { NotificationButton } from '../Notifications/Notifications'
import DashboardsNavPopover from './DashboardsNavPopover/DashboardsNavPopover'
import { isPageWithoutNav } from './Nav.helpers'
import NavContent from './NavContent'
import NavGlobalSearchModal from './NavGlobalSearchModal'
import PreviewModeButton from './PreviewModeButton'
import UserSettings from './UserSettings/UserSettings'

const MainNav = () => {
  const location = useLocation()

  const showBrokerAccounting = useFlag('feature.report_commissions', false)

  const { isVisible: isModalSearchVisible, open: openModalSearch, close: closeModalSearch } = useOpen(false)

  const { trackEvent } = useMatomo()

  useKeyboardShortcut('k', () => {
    if (isModalSearchVisible) return
    trackEventFiltered(trackEvent, location, 'searchAction', 'openNewSearchModalViaKeyboard')
    openModalSearch()
  })

  const user = useUserContext()
  const theme = useTheme()
  const hideClientList = useFlag('feature.hideClientList', false)
  const isScoreRebrand = useFlag('feature.score_rebrand', false)

  const client = useApolloClient()

  // Start polling for notifications from socket
  const { clearNotificationsById, clearAllNotifcations } = useNotifications(
    client as ApolloClient<NormalizedCacheObject>,
  )

  const { data: notifications } = useGetNotificationsQuery({
    variables: { userId: String(user?.id) },
    skip: !user?.id,
  })

  const { data, loading } = useGetCurrentUserQuery()

  const sourcingUrl = useSourcingUrl()

  const { userOrganisationIds, userId } = useMemo(() => {
    if (!user) {
      return {
        userOrganisationIds: [],
        userId: [],
      }
    }

    return {
      userOrganisationIds: [getUserOrganisationIds({ user }).userOrganisationId],
      userId: [user.id],
    }
  }, [user])

  const permissionInputs: PermissionInput[] = [
    {
      gates: [
        'VIEW_BROKER_DASHBOARD',
        'VIEW_CLIENT_SECTION',
        'ACCOUNTING',
        'EDIT_CLIENT_RESTRICTED',
        'VIEW_CASE_COMPLIANCE',
        'VIEW_MI_DASHBOARD',
      ],
      entityIdsCollection: {
        organisation: userOrganisationIds,
      },
    },
    {
      gates: ['VIEW_CASE_SECTION'],
      entityIdsCollection: {
        organisation: userOrganisationIds,
        user: userId,
      },
    },
    {
      gates: ['ACCOUNTING'],
      entityIdsCollection: {
        user: userId,
      },
    },
  ]

  const { gates, gatesByScope } = usePermissionResolution({
    inputs: permissionInputs,
  })

  const accountingNavLink = useMemo(() => {
    if (gatesByScope.organisation?.has('ACCOUNTING')) {
      return '/accounting/payments-due'
    }

    if (showBrokerAccounting && gatesByScope.user?.has('ACCOUNTING')) {
      return '/accounting/commission-statement'
    }

    return null
  }, [gatesByScope, showBrokerAccounting])

  const reminderRoles = [
    userType.principal,
    userType.administrator,
    userType.advisor,
    userType.firmManager,
    userType.modifySupport,
    userType.readOnlySupport,
  ]

  const displayComplianceLink = gates.includes('VIEW_CASE_COMPLIANCE') && !gates.includes('VIEW_CASE_SECTION')

  if (isPageWithoutNav(location?.pathname)) {
    return null
  }

  return (
    <>
      {loading && <FullPageLoadingSpinner />}

      {isModalSearchVisible && (
        <NavGlobalSearchModal isModalSearchVisible={isModalSearchVisible} closeModalSearch={closeModalSearch} />
      )}

      {Boolean(data) && (
        <Nav
          offset={theme.display.navHeightSmall.toString()}
          left={[
            <Box key="logo" display="flex" alignItems="center" ml={4} mr={12 / 8}>
              {isScoreRebrand ? <ScoreWordMarkHover height={24} /> : <AcreSquareLogo />}
            </Box>,
            <DashboardsNavPopover gates={gates} key="DashboardsNavPopover" />,
            gates.includes('VIEW_CASE_SECTION') && (
              <OptionsNavLink
                to="/cases/board-view"
                id="MainNavCases"
                key="MainNavCases"
                isActive={(location) => location.startsWith('/cases')}
                alwaysRedirect
              >
                <FormattedMessage id="nav.cases" />
              </OptionsNavLink>
            ),
            displayComplianceLink && (
              <OptionsNavLink
                to="/cases/compliance-view"
                id="ComplianceCasesLink"
                key="ComplianceCasesLink"
                isActive={(location) => location.endsWith('/cases/compliance-view')}
                alwaysRedirect
              >
                <FormattedMessage id="nav.casesCompliance" />
              </OptionsNavLink>
            ),
            gates.includes('VIEW_CLIENT_SECTION') && !(hideClientList && user?.primary_role_id == userType.advisor) && (
              <OptionsNavLink
                to="/clients"
                id="MainNavClientsLink"
                key="MainNavClientsLink"
                isActive={(location) => location.startsWith('/clients')}
                alwaysRedirect
              >
                <FormattedMessage id="nav.clients" />
              </OptionsNavLink>
            ),
            user?.primary_role_id && reminderRoles.includes(user.primary_role_id) && (
              <OptionsNavLink
                to="/reminders"
                id="RemindersLink"
                key="RemindersLink"
                isActive={(location) => location.startsWith('/reminders')}
                alwaysRedirect
              >
                <FormattedMessage id="nav.reminders" />
              </OptionsNavLink>
            ),

            <Flag keyPath="feature.mi" key="ReportsFlag" defaultValue={true}>
              <OptionsNavLink
                to={`/reports/${user?.organisation_id}`}
                id="ReportsLink"
                isActive={(location) => location.startsWith('/reports')}
                alwaysRedirect
              >
                <FormattedMessage id="nav.reports" />
              </OptionsNavLink>
            </Flag>,

            // Allow for unique keys in each array element
            ...(user?.primary_role_id && reminderRoles.includes(user.primary_role_id)
              ? [
                  <ExternalLink href={sourcingUrl} key={sourcingUrl}>
                    <FormattedMessage id="nav.sourcing.title" />
                  </ExternalLink>,
                ]
              : []),
            accountingNavLink && (
              <OptionsNavLink
                to={accountingNavLink}
                id="MainNavAccountingLink"
                key="MainNavAccountingLink"
                isActive={(location) => location.startsWith('/accounting')}
                alwaysRedirect
              >
                <FormattedMessage id="nav.accounting" />
              </OptionsNavLink>
            ),
          ]}
          right={
            <Box display="flex" alignItems="center" mr={3} ml={2} gap={2}>
              <GlobalSearchFieldAnchor
                onClick={() => {
                  openModalSearch()
                }}
                placeholder="Search"
              />
              <PreviewModeButton />
              <NotificationButton
                notifications={notifications?.notifications || []}
                onClearAll={clearAllNotifcations}
                onClearById={clearNotificationsById}
                key="NavGlobalNotifications"
              />
              <UserSettings
                firstName={user?.first_name}
                lastName={user?.last_name}
                organisationId={user?.organisation_id}
                userId={user?.id}
              />
            </Box>
          }
        >
          <NavContent pathname={location?.pathname} />
        </Nav>
      )}
    </>
  )
}

export default MainNav
